import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import { useSelector } from "react-redux";

import {
  DEFAULT_LEFT_NAV_PATH,
  shouldShowSubmissionHistory,
} from "./shellRoutes";
import { LeftNav } from "./LeftNav";
import { AppBreadcrumbs } from "./AppBreadcrumbs";
import { FilterBar } from "../filterBar/FilterBar";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import { ValidationDashboardPage } from "../validation/ValidationDashboardPage";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";
import { StamperWizard } from "../stampUtils/StamperWizard";
import { LoomAgentAssistantWidget } from "../../components/loom-agent-assistant/LoomAgentAssistantWidget";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { InitializeRum } from "../rum/initializeRum";
import { Notification } from "../notification/notification";
import { CacheableNotification } from "../notification/cacheableNotification";
import { StaleDataAlert } from "../validation/StaleDataAlert";
import { NewViewOnboardingAlert } from "./NewViewOnboardingAlert";
import { V2Toggle } from "../app/V2Toggle";
import { ToolbarPortal } from "../app/ToolbarPortal";
import { trackButtonClick } from "../telemetry/telemetry";

// Interaction-only: the submission list is only needed once the drawer
// is actually opened, so it stays code-split. Legacy `<Content/>` lazy-
// loads the same module; a static import here would pull it into the
// main chunk and undo that for Legacy users too. Named export mapped
// onto the default export `React.lazy` expects.
const OclSubmissionTools = lazy(() =>
  import("../oneClickLaunch/oneClickLaunchSubmissionTools").then((module) => ({
    default: module.OclSubmissionTools,
  }))
);

const AGENT_ASSISTANT_DRAWER_ID = "agent-assistant";
const SUBMISSION_HISTORY_DRAWER_ID = "submission-history";

const AGENT_ASSISTANT_DRAWER_STYLE: React.CSSProperties = {
  height: "100%",
  width: "100%",
  boxSizing: "border-box",
};

const SUBMISSION_HISTORY_DRAWER_STYLE: React.CSSProperties = {
  padding: "20px",
};

/** Shown while the code-split drawer chunk is in flight. */
const DrawerLoadingFallback: React.FC = () => (
  <div className="awsui-util-t-c awsui-util-mt-l" data-testid="drawer-loading">
    <Spinner size="large" />
  </div>
);

/** App Shell root. Owns routing; the layout reads the active route. */
export const AppShell: React.FC = () => (
  <BrowserRouter>
    <AppShellLayout />
  </BrowserRouter>
);

/**
 * FilterBar mounts above `<Routes/>` so it survives intra-shell nav
 * without remount. Drawer config mirrors Legacy. Lives inside
 * `<BrowserRouter>` so the drawer set can depend on the active route.
 */
const AppShellLayout: React.FC = () => {
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );
  const location = useLocation();
  const showSubmissionHistory = shouldShowSubmissionHistory(location.pathname);

  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      if (detail.activeDrawerId === SUBMISSION_HISTORY_DRAWER_ID) {
        trackButtonClick("appShell", "openSubmissionHistory");
      }
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  // Navigating away from `/launch` removes the drawer from `drawers`.
  // Clear the selection so the layout doesn't keep a panel open for a
  // drawer that no longer exists.
  useEffect(() => {
    if (!showSubmissionHistory) {
      setActiveDrawerId((current) =>
        current === SUBMISSION_HISTORY_DRAWER_ID ? null : current
      );
    }
  }, [showSubmissionHistory]);

  const drawers = useMemo(
    () => [
      ...(isLoomAgentAssistantEnabled
        ? [
            {
              id: AGENT_ASSISTANT_DRAWER_ID,
              ariaLabels: {
                drawerName: "Loom Agent Assistant",
                closeButton: "Close Loom Agent Assistant",
                triggerButton: "Open Loom Agent Assistant",
                resizeHandle: "Resize Loom Agent Assistant panel",
              },
              trigger: { iconName: "gen-ai" as const },
              resizable: true,
              defaultSize: 400,
              content: (
                <div
                  data-testid="agent-assistant-drawer"
                  style={AGENT_ASSISTANT_DRAWER_STYLE}
                >
                  <LoomAgentAssistantWidget />
                </div>
              ),
            },
          ]
        : []),
      ...(showSubmissionHistory
        ? [
            {
              id: SUBMISSION_HISTORY_DRAWER_ID,
              ariaLabels: {
                drawerName: "Submission History",
                closeButton: "Close submission history",
                triggerButton: "Open submission history",
                resizeHandle: "Resize submission history panel",
              },
              trigger: { iconName: "status-info" as const },
              resizable: true,
              defaultSize: 420,
              content: (
                <div
                  data-testid="submission-history-drawer"
                  style={SUBMISSION_HISTORY_DRAWER_STYLE}
                >
                  <Suspense fallback={<DrawerLoadingFallback />}>
                    <OclSubmissionTools />
                  </Suspense>
                </div>
              ),
            },
          ]
        : []),
    ],
    [isLoomAgentAssistantEnabled, showSubmissionHistory]
  );

  return (
    <>
      {/* Portal the toggle next to the AI-drawer trigger so it shares
          the toolbar's sticky + rubber-band behavior. */}
      <ToolbarPortal>
        <V2Toggle />
      </ToolbarPortal>
      <AppLayoutToolbar
        data-testid="app-shell-layout"
        // Match Legacy: offset the sticky toolbar below the mosaic
        // strip so it doesn't hide behind it on scroll. Only the
        // toolbar itself is sticky here — FilterBar and notification
        // banners live inside the routed content and scroll away with
        // the page, so the full viewport height under the toolbar is
        // available for the routed table.
        headerSelector={"#mosaic-navigation"}
        footerSelector={"#footer"}
        navigation={<LeftNav />}
        breadcrumbs={<AppBreadcrumbs />}
        contentType="table"
        toolsHide={true}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={drawers}
        content={<AppShellRoutedContent />}
      />
    </>
  );
};

// FilterBar drives ASIN-list filtering; Stamper Tool is a distinct
// workflow that doesn't consume that state, so we skip the bar on
// `/stamper` to avoid a stale filter UI above an unrelated wizard.
const PATHS_WITHOUT_FILTER_BAR: ReadonlySet<string> = new Set(["/stamper"]);

const AppShellRoutedContent: React.FC = () => {
  const location = useLocation();
  const showFilterBar = !PATHS_WITHOUT_FILTER_BAR.has(location.pathname);

  // Redirect target that preserves `search` and `hash` from the
  // current URL. The Applied Filter is encoded in the hash, and the
  // Legacy `sumQueries` output plus tab-id live in search — a plain
  // `<Navigate to="/search" />` drops both. Path objects (`{pathname,
  // search, hash}`) let react-router carry them through the replace.
  const redirectTo = useMemo(
    () => ({
      pathname: DEFAULT_LEFT_NAV_PATH,
      search: location.search,
      hash: location.hash,
    }),
    [location.search, location.hash]
  );

  return (
    <div data-testid="app-shell-content">
      <InitializeRum />
      <SpaceBetween size="l" direction="vertical">
        {showFilterBar && <FilterBar />}
        <div data-testid="notifications-container">
          {/* First-visit how-to for the shell layout. Gated with the
              FilterBar since its CTA focuses that input. */}
          {showFilterBar && <NewViewOnboardingAlert />}
          <Notification />
          <CacheableNotification />
          <StaleDataAlert />
        </div>
        <Routes>
          <Route path="/" element={<Navigate to={redirectTo} replace />} />
          <Route path="/search" element={<PorAsinListTable />} />
          <Route path="/baseline" element={<BaselineTable />} />
          <Route path="/validation" element={<ValidationDashboardPage />} />
          <Route path="/launch" element={<OneClickLaunchTable />} />
          <Route path="/stamper" element={<StamperWizard />} />
          <Route path="*" element={<Navigate to={redirectTo} replace />} />
        </Routes>
      </SpaceBetween>
    </div>
  );
};

export default AppShell;
